import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { MotifJustification } from './useAbsences';

/**
 * Justification des absences depuis l'espace apprenant (CDC §6.6, §8.1) :
 * l'apprenant dépose un justificatif pour une ou plusieurs demi-journées, le
 * pôle pédagogique l'accepte ou le refuse en motivant.
 */

export interface DemiJourneeAJustifier {
  presenceId: string;
  jour: string;
  libelle: string;
  seance: string;
}

export interface JustificatifApprenant {
  id: string;
  motif: string;
  motifLibelle: string;
  precision: string | null;
  nomFichier: string;
  deposeLe: string;
  statut: 'A_EXAMINER' | 'ACCEPTE' | 'REFUSE';
  statutLibelle: string;
  statutTon: string;
  motifRefus: string | null;
  decideLe: string | null;
  demiJournees: string[];
  refusRetrait: string | null;
}

export interface VueJustification {
  aJustifier: DemiJourneeAJustifier[];
  justificatifs: JustificatifApprenant[];
  motifs: MotifJustification[];
  formats: string;
}

const CLE = ['espace-apprenant', 'absences'];

export function useMesJustificatifs() {
  return useQuery({
    queryKey: CLE,
    queryFn: () => appelApi<VueJustification>('/espace-apprenant/absences'),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
}

export function useDeposerJustificatif() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: { motif: string; precision: string; presenceIds: string[]; fichier: File }) => {
      const formulaire = new FormData();
      formulaire.append('motif', saisie.motif);
      if (saisie.precision.trim()) formulaire.append('precision', saisie.precision.trim());
      formulaire.append('presenceIds', JSON.stringify(saisie.presenceIds));
      formulaire.append('fichier', saisie.fichier);
      return appelApi<VueJustification>('/espace-apprenant/absences/justificatifs', {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => invalider(client),
  });
}

export function useRetirerJustificatif() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<VueJustification>(`/espace-apprenant/absences/justificatifs/${encodeURIComponent(id)}`, {
        methode: 'DELETE',
      }),
    onSuccess: () => invalider(client),
  });
}

export function urlMonJustificatif(id: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/espace-apprenant/absences/justificatifs/${encodeURIComponent(id)}/fichier`;
}
